<template>
  <b-modal
    :active.sync="isModalActive"
    has-modal-card
    full-screen
    ref="certificateManualMatchingModal"
    :can-cancel="false"
  >
    <div class="modal-card enable-select-text">
      <header class="modal-card-head">
        <h1 class="is-title-header modal-card-title">
          Certificate Manual Matching
        </h1>
      </header>
      <section
        class="modal-card-body manual-matching-modal-body"
        style="padding-top: 10px; display: flex; flex-direction: column;"
      >
        <div
          class="table-container"
          :style="{ height: tableHeight + 'px', maxHeight: tableHeight + 'px' }"
        >
          <certificateMappingTable
            :customerId="customerId"
            @selectedRows="handleSelectedCertificateRows"
            :newTableHeight="tableHeight - 80"
            :matchClicked="matchClickCount"
          ></certificateMappingTable>
        </div>
        <hr style="margin: 0.5rem" />
        <!-- Product Table Container with flex-grow -->
        <div
          class="table-container"
          :style="{
            height: tableHeight + 'px',
            maxHeight: tableHeight + 'px'
          }"
        >
          <productCertificateMappingTable
            @selectedRows="handleSelectedProductRows"
            :newTableHeight="tableHeight - 80"
            :matchClicked="matchClickCount"
          ></productCertificateMappingTable>
        </div>
      </section>
      <footer class="modal-card-foot" style="padding: 10px 20px;">
        <b-button
          slot="right"
          type="is-primary"
          @click="matchData()"
          :disabled="isMatchDisabled"
        >
          Match
        </b-button>
        <b-button slot="right" type="is-danger" @click="cancel">
          Cancel
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import productCertificateMappingTable from "./ProductMappingTable.vue";
export default {
  name: "CertificateManualMatching",
  props: {
    isActive: { type: Boolean, default: false }
  },
  components: {
    productCertificateMappingTable,
    certificateMappingTable: () => import("./CertificateMappingTable.vue")
  },
  data() {
    return {
      form: {
        product: [],
        certificate: [],
        product_customer_id: null
      },
      isModalActive: false,
      tableHeight: 300, // Default height that will be calculated
      debounceTimeout: null,
      matchClickCount: 0, // Counter to track match button clicks
      isSaving: false
    };
  },
  computed: {
    isMatchDisabled() {
      // Force recalculation by accessing length properties
      const productLength = this.form.product ? this.form.product.length : 0;
      const certificateLength = this.form.certificate
        ? this.form.certificate.length
        : 0;
      return productLength === 0 || certificateLength === 0 || this.isSaving;
    }
  },
  methods: {
    // Method to trigger refresh from parent after successful match
    triggerRefresh() {
      this.matchClickCount++;
    },
    handleSelectedProductRows(selectedRows, customerId) {
      // Use $set to ensure reactivity
      this.$set(this.form, "product", [...selectedRows]);
      this.$set(this.form, "product_customer_id", customerId || null);
    },
    handleSelectedCertificateRows(selectedRows) {
      // Use $set to ensure reactivity
      this.$set(this.form, "certificate", [...selectedRows]);
    },
    matchData() {
      if (!this.form.product?.length || !this.form.certificate?.length) {
        this.$buefy.dialog.alert({
          title: "Error",
          message: "Please select both product and certificate data to match.",
          type: "is-danger"
        });
        return;
      }
      if (!this.form.product_customer_id) {
        this.$buefy.dialog.alert({
          title: "Error",
          message:
            "Please select a customer in the products table before matching.",
          type: "is-danger"
        });
        return;
      }
      this.isSaving = true;
      this.$emit("matchedData", { ...this.form });
    },
    onMatchSuccess() {
      // Called by parent after API succeeds — refresh tables and clear selections
      this.isSaving = false;
      this.form.product = [];
      this.form.certificate = [];
      this.form.product_customer_id = null;
      this.matchClickCount++;
      this.$buefy.toast.open({
        message: "Match completed successfully!",
        type: "is-success",
        duration: 2000
      });
    },
    onMatchFailed() {
      // Called by parent after API fails — restore UI so user can retry or cancel
      this.isSaving = false;
    },
    cancel() {
      this.form = {
        product: [],
        certificate: [],
        product_customer_id: null
      };
      this.$emit("cancel");
    },
    calculateTableHeight() {
      // Get window height
      const windowHeight = window.innerHeight;

      // Calculate available space for tables (modal body)
      // Subtract header (60px), footer (60px), hr (10px), padding (~40px)
      const availableHeight = windowHeight - 220;

      // Divide by 2 for two tables
      this.tableHeight = Math.floor(availableHeight / 2);
    },
    handleResize() {
      // Clear the previous timeout to debounce rapid resize events
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      // Set a new timeout
      this.debounceTimeout = setTimeout(() => {
        this.calculateTableHeight();
      }, 200);
    }
  },
  watch: {
    isActive(newValue) {
      this.isModalActive = newValue;

      if (newValue) {
        // When modal becomes active, calculate height
        this.$nextTick(() => {
          this.calculateTableHeight();
        });
      }
    },
    isModalActive(newValue) {
      if (!newValue) {
        this.cancel();
      }
    }
  },
  mounted() {
    // Add resize event listener
    window.addEventListener("resize", this.handleResize);

    // Initial calculation
    this.$nextTick(() => {
      this.calculateTableHeight();
    });
  },
  beforeDestroy() {
    // Remove resize event listener
    window.removeEventListener("resize", this.handleResize);
    // Clear any pending timeouts
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }
};
</script>

<style>
.manual-matching-modal-body {
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}

.table-container {
  overflow: hidden;
  transition: height 0.3s, max-height 0.3s;
}

/* Ensure content fits within the container */
.has-listing-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
</style>
